import { useSession, useSignOut } from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import { Button } from '@numistic/ui'
import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import { useCallback, useState } from 'react'

import { ApiStatus } from '../components/api-status.js'
import { HotkeysHelp, useHotkeys } from '../lib/hotkeys.js'
import type { RouterContext } from '../lib/context.js'

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  notFoundComponent: NotFound,
})

function NotFound() {
  const { t } = useTranslation()
  return <p className="text-muted-foreground">{t('errors.notFound')}</p>
}

function RootLayout() {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const signOut = useSignOut()
  const navigate = useNavigate()
  const [help, setHelp] = useState(false)
  const [menu, setMenu] = useState(false)
  const openHelp = useCallback(() => setHelp(true), [])
  const closeHelp = useCallback(() => setHelp(false), [])
  useHotkeys(openHelp)
  // Меню на телефоне закрывается при переходе на другую страницу.
  const path = useRouterState({ select: (s) => s.location.pathname })
  const [menuPath, setMenuPath] = useState(path)
  if (menuPath !== path) {
    setMenuPath(path)
    setMenu(false)
  }

  const navLink =
    'rounded-md px-2 py-1.5 text-muted-foreground hover:text-foreground [&.active]:text-foreground [&.active]:font-medium'
  const links = (
    <>
      <Link to="/catalog" search={{}} className={navLink}>
        {t('nav.catalog')}
      </Link>
      {session ? (
        <>
          <Link to="/c" search={{}} className={navLink}>
            {t('nav.collections')}
          </Link>
          <Link to="/stats" search={{}} className={navLink}>
            {t('nav.stats')}
          </Link>
        </>
      ) : null}
      {session?.user.role === 'admin' ? (
        <Link to="/admin/users" className={navLink}>
          {t('nav.admin')}
        </Link>
      ) : null}
    </>
  )
  const account = session ? (
    <>
      <Link to="/settings/profile" className={navLink} title={t('settings.title')}>
        {session.user.name}
      </Link>
      <Button
        variant="outline"
        size="sm"
        disabled={signOut.isPending}
        onClick={() => signOut.mutate(undefined, { onSuccess: () => void navigate({ to: '/' }) })}
      >
        {t('nav.signOut')}
      </Button>
    </>
  ) : (
    <Button asChild size="sm">
      <Link to="/login">{t('nav.signIn')}</Link>
    </Button>
  )

  return (
    <div className="flex min-h-dvh flex-col">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-card focus:px-3 focus:py-2 focus:shadow"
      >
        {t('a11y.skipToContent')}
      </a>
      <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-4 py-2.5">
          <Link to="/" className="mr-2 text-lg font-semibold tracking-tight">
            {t('app.name')}
          </Link>
          <nav className="hidden items-center gap-1 text-sm md:flex" aria-label={t('a11y.mainNav')}>
            {links}
          </nav>
          <div className="ml-auto flex items-center gap-2 text-sm">
            <ApiStatus />
            <div className="hidden items-center gap-2 md:flex">{account}</div>
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              aria-expanded={menu}
              aria-controls="mobile-menu"
              aria-label={menu ? t('nav.closeMenu') : t('nav.menu')}
              onClick={() => setMenu((v) => !v)}
            >
              <span aria-hidden className="text-lg leading-none">
                {menu ? '✕' : '☰'}
              </span>
            </Button>
          </div>
        </div>
        {menu ? (
          <div id="mobile-menu" className="border-t px-4 pb-4 pt-2 md:hidden">
            <nav className="grid gap-1 text-base" aria-label={t('a11y.mainNav')}>
              {links}
            </nav>
            <div className="mt-3 flex items-center gap-3 border-t pt-3 text-sm">{account}</div>
          </div>
        ) : null}
      </header>
      <main
        id="main"
        tabIndex={-1}
        className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 outline-none sm:py-8"
      >
        <Outlet />
      </main>
      <footer className="mx-auto hidden w-full max-w-6xl px-4 pb-6 text-xs text-muted-foreground md:block">
        <button type="button" className="hover:text-foreground" onClick={openHelp}>
          {t('hotkeys.hint')}
        </button>
      </footer>
      {help ? <HotkeysHelp onClose={closeHelp} /> : null}
    </div>
  )
}
